import React, { ProviderProps, useState } from "react";
import { ESocialChannel, IOffice, IPerson } from "../../lib/Contact";
import { MdOutlinePhonelinkRing } from "react-icons/md";
import { FiPhone } from "react-icons/fi";
import { IconContext } from "react-icons";
import { FaFacebookSquare, FaGlobe, FaInstagram, FaLinkedin, FaRegAddressBook, FaTwitter, FaTwitterSquare, FaYoutubeSquare } from "react-icons/fa";
import { RiMailSendLine, RiMapPinLine } from "react-icons/ri";
import { WsrvImage } from "../Media";
import { EWsrvTransform } from "../../lib";
import { Button } from "../Button";
import { ILinkFunc } from "../../lib/util";
import { formatAddress } from "../../lib/Contact/util";
import { ContactForm, IContactFormProps } from "../Forms";
import { LazyLoadImageProps } from "react-lazy-load-image-component";

export enum EContactCardLayout {
  vertical = "vertical",
  horizontal = "horizontal",
  offset = "offset",
  pageSection = "pageSection",
  twoColsAndForm = "twoColsAndForm",
  threeColsAndForm = "threeColsAndForm",
}

export enum ECardIconsLayout {
  vertical = "vertical",
  columns = "columns",
  columns1row = "columns1row",
}

export interface EContactCardDetails {
  workTitle: boolean;
  phoneMobile: boolean;
}

export enum EOfficeInfoPosition {
  card = "card",
  bio = "bio",
}

export interface IContactCardProps {
  contact: IPerson;
  showSocial?: boolean;
  showImage?: boolean;
  showVCard?: boolean;
  showEmail?: boolean;
  showAddress?: boolean;
  showBio?: boolean;
  showWebsite?: boolean;
  className?: string;
  layout?: EContactCardLayout;
  contactInfoItemsLayout?: ECardIconsLayout;
  iconAccent?: boolean;
  maxWidthImage?: number;
  imageClasses?: string;
  buttonText?: string;
  linkFunc?: ILinkFunc;
  formTitle?: string;
  formTextAreaLabel?: string;
  showOffices?: boolean;
  offices?: IOffice[];
  officesWrapperClassNames?: string;
  formProps?: Partial<IContactFormProps>;
  imageWsrvTransform?: EWsrvTransform;
  imageProps?: Partial<LazyLoadImageProps>;
  contactUnderPhoto?: boolean;
  showAgentBioReadMoreButton?: boolean;
  agentInfoClassName?: string;
  addressClassNames?: string;
  officeInfoPosition?: EOfficeInfoPosition;
  showLicense?: boolean;
  imageLinkClassNames?: string;
  /**
   * For vertical layout only
  */
  showWorkTitle?: boolean;
  showOfficePhone?: boolean;
  showMobilePhone?: boolean;
  showContactInfoOnFormSubmit?: boolean;
}

export const ContactCard: React.FC<IContactCardProps> = ({
  contact,
  showSocial = true,
  showWebsite = false,
  showVCard = true,
  showOfficePhone = true,
  showMobilePhone = true,
  showAddress = false,
  showBio = false,
  layout = EContactCardLayout.horizontal,
  contactInfoItemsLayout = ECardIconsLayout.vertical,
  showImage = true,
  className,
  iconAccent = true,
  maxWidthImage = 400,
  imageClasses,
  buttonText,
  linkFunc,
  formTitle = "",
  formTextAreaLabel = "Add a message here...",
  showOffices = false,
  offices = [],
  formProps,
  imageWsrvTransform = EWsrvTransform.thumb3x4,
  contactUnderPhoto = false,
  showEmail = true,
  imageProps,
  showAgentBioReadMoreButton = true,
  agentInfoClassName = "",
  officesWrapperClassNames = "",
  addressClassNames = "",
  officeInfoPosition = EOfficeInfoPosition.card,
  showLicense = false,
  imageLinkClassNames = "",
  showWorkTitle = false,
  showContactInfoOnFormSubmit = false,
}) => {
  const [showMoreBio, setShowMoreBio] = useState(false)

  let layoutVariant = {
    containerClasses: "",
    imageWrapperClasses: "",
    textClasses: "",
    infoClasses: "",
    formClasses: "",
  };

  switch (layout) {
    case EContactCardLayout.horizontal:
      layoutVariant = {
        ...layoutVariant,
        containerClasses: "grid gap-1 grid-cols-3",
        infoClasses: "flex flex-col gap-1 col-span-2",
      };
      break;
    case EContactCardLayout.vertical:
      layoutVariant = {
        ...layoutVariant,
        containerClasses: "inline-flex flex-col",
        textClasses: "text-sm",
        infoClasses: "flex flex-col items-center gap-1",
      };
      break;
    case EContactCardLayout.pageSection:
      layoutVariant = {
        ...layoutVariant,
        containerClasses: "grid md:gap-4 xl:gap-20 grid-cols-1 md:grid-cols-3",
        infoClasses: "flex flex-col justify-center gap-5 md:col-span-2 md:pr-4 xl:pr-12",
      };
      break;
    case EContactCardLayout.threeColsAndForm:
      layoutVariant = {
        ...layoutVariant,
        containerClasses: "p-2 lg:p-11 grid gap-y-2 gap-x-0 md:gap-x-2 lg:gap-9 grid-cols-12",
        imageWrapperClasses: "col-span-12 md:col-span-6 lg:col-span-4",
        infoClasses: "col-span-12 md:col-span-6 lg:col-span-3",
        formClasses: "col-span-12 lg:col-span-5",
      };
      break;
    case EContactCardLayout.twoColsAndForm:
      layoutVariant = {
        ...layoutVariant,
        containerClasses: "p-2 lg:px-11 lg:py-7 grid gap-y-2 gap-x-0 md:gap-x-2 lg:gap-x-9 grid-cols-12",
        imageWrapperClasses: "col-span-12 md:col-span-12 lg:col-span-5",
        infoClasses: "col-span-12 md:col-span-12 lg:col-span-7 lg:row-span-2",
        formClasses: "col-span-12 lg:col-span-5",
      };
      break;
  }

  const Linkf = linkFunc || ((props) => <a {...props} />);

  let image: React.ReactNode = showImage && contact && contact.photos?.length && (
    <WsrvImage
      urlParams={{
        originURL: contact.photos[0].url,
        preset: imageWsrvTransform,
        width: maxWidthImage,
      }}
      widthMax={maxWidthImage}
      imgProps={{
        className: `max-w-full min-h-full object-cover mx-auto ${imageClasses}`,
        title: contact.fullName,
        wrapperClassName: layoutVariant.imageWrapperClasses,
        style: {
          maxWidth: "100%",
          minWidth: "100%",
          minHeight: "100%",
          objectFit: "cover",
          margin: "0 auto",
        },
        ...imageProps
      }}
    />
  );

  image &&
    linkFunc &&
    (image = (
      <Linkf
        href={contact.path}
        title={contact.fullName}
        className={`text-center ${imageLinkClassNames}`}
      >
        {image}
      </Linkf>
    ));

  let fullName: React.ReactNode = contact.fullName;
  linkFunc &&
    (fullName = (
      <Linkf href={contact.path} title={contact.fullName}>
        {fullName}
      </Linkf>
    ));

  //Title
  const cardTitle = (<>
    <h2 className={`p-0 text-inherit text-[1.5em]`}>{fullName}</h2>
    {layout != EContactCardLayout.vertical &&
      (contact.workTitle || contact.designations ? (
        <div>
          {contact.workTitle && <h3 className="p-0 text-inherit text-[1.17em]">
            {contact.workTitle}
          </h3>}
          {contact.designations && <h3 className="p-0 text-inherit text-[1.17em] leading-none">
            {contact.designations}
          </h3>}
        </div>
      ) : (
        <hr className="w-16 border-2 text-inherit" />
      ))}
    {layout != EContactCardLayout.vertical && showLicense &&
      contact.license && (
        <h3 className="p-0 text-inherit text-[1.17em]">
          {contact.license}
        </h3>
      )}
  </>);

  const cardTitleContainer = layout == EContactCardLayout.pageSection ?
    (<div>{cardTitle}</div>) : (<>{cardTitle}</>)
  //

  const [_showOfficePhone, setShowOfficePhone] = useState(showOfficePhone);
  const [_showMobilePhone, setShowMobilePhone] = useState(showMobilePhone);
  const [_showEmail, setShowEmail] = useState(showEmail);

  //Contact info
  const mobilePhoneInfo = _showMobilePhone && contact.phoneMobile ?
    (<div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
      <a href={`tel: ${contact.phoneMobile}`}>
        <MdOutlinePhonelinkRing />
      </a>
      {contact.phoneMobile}
    </div>) : undefined;

  const officePhoneInfo = _showOfficePhone && contact.phoneOffice ?
    (<div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
      <a href={`tel: ${contact.phoneOffice}`}>
        <FiPhone />
      </a>
      {contact.phoneOffice}
    </div>) : undefined;

  const emailInfo = contact.email && _showEmail ?
    (<div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
      <a href={`mailto: ${contact.email}`}>
        <RiMailSendLine />
      </a>
      {contact.email}
    </div>) : undefined;

  const addresInfo = contact.address && showAddress ?
    (<div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full flex flex-row">
      <RiMapPinLine />
      <span className="whitespace-pre">{formatAddress(contact.address, true)}</span>
    </div>) : undefined;

  const vCard = showVCard ?
    (<div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
      <a href="#">
        <FaRegAddressBook />
        <span className="text-inherit hover:text-inherit">
          Add to Contacts
        </span>
      </a>
    </div>) : undefined;

  const websiteInfo = showWebsite && contact.personalWebsite ?
    (<div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
      <a target="_blank" href={contact.personalWebsite}>
        <FaGlobe />
        <span className="text-inherit hover:text-inherit">Personal Website</span>
      </a>
    </div>) : undefined;

  const getSocialMediaIcon = (name: ESocialChannel | string) => {
    switch (name.toLowerCase()) {
      case "facebook":
        return <FaFacebookSquare className="!mx-0" />;
      case "instagram":
        return <FaInstagram className="!mx-0" />;
      case "linkedin":
        return <FaLinkedin className="!mx-0" />;
      case "youtube":
        return <FaYoutubeSquare className="!mx-0" />;
      case "twiter":
        return <FaTwitterSquare className="!mx-0" />;
      default:
        return undefined;
    }
  }

  const socialInfo = showSocial && contact.social && contact.social.length > 0 ?
    (
      <div className="flex flex-row flex-nowrap gap-x-2">
        {contact.social.map(item => {
          return (<a className="inline-block" target="_blank" href={item.url}>
            {getSocialMediaIcon(item.channel ?? "")}
          </a>)
        })}
      </div>
    ) : undefined;

  let officeInfoCss = {
    wrapperClasses: "justify-between py-7",
    addressClasses: "w-1/2",
    addressTitleClasses: "",
    addressContentClasses: "",
  }
  if (layout == EContactCardLayout.twoColsAndForm) {
    officeInfoCss = {
      wrapperClasses: "justify-start grow gap-x-7",
      addressClasses: "",
      addressTitleClasses: "xl:text-center",
      addressContentClasses: "xl:text-center",
    }
  }

  const officesInfo = showOffices && (offices.length > 0 || (contact.officeName && contact.officeName.length > 0)) && (
    <div
      className={`w-full flex flex-row flex-wrap ${officeInfoCss.wrapperClasses} ${officesWrapperClassNames}`}>
      {offices && offices.length > 0 && offices.map((office, index) => (
        <address
          key={index}
          className={`not-italic ${officeInfoCss.addressClasses} ${addressClassNames}`}
        >
          <div className={`font-bold uppercase pb-1.5 ${officeInfoCss.addressTitleClasses}`}>
            {office.fullName}
          </div>
          <div className={`leading-none w-full text-sm ${officeInfoCss.addressContentClasses}`}>
            <div className="pb-1">{office.address?.street1}</div>
            <div className="pb-1">
              {office.address?.city}, {office.address?.state}{" "}
              {office.address?.postal}
            </div>
            {office.phone && layout == EContactCardLayout.threeColsAndForm && (
              <div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full pt-1.5">
                <a href={`tel: ${office.phone}`}>
                  <FiPhone />
                </a>
                {office.phone}
              </div>
            )}
          </div>
        </address>
      ))}
      {(!offices || offices.length == 0) && (contact.officeName && contact.officeName.length > 0) && (
        <address
          className={`not-italic ${officeInfoCss.addressClasses} ${addressClassNames}`}
        >
          <div className={`font-bold uppercase pb-1.5 ${officeInfoCss.addressTitleClasses}`} dangerouslySetInnerHTML={{ __html: contact.officeName }}></div>
        </address>
      )}
    </div>
  )

  const contactContent = (() => {
    switch (contactInfoItemsLayout) {
      case ECardIconsLayout.vertical:
        let infoItems = (
          <>
            {showOffices && officeInfoPosition == EOfficeInfoPosition.card && officesInfo}
            {mobilePhoneInfo}
            {officePhoneInfo}
            {emailInfo}
            {addresInfo}
            {vCard}
            {socialInfo}
            {showWorkTitle && <span className="font-semibold uppercase">{contact.workTitle}</span>}
          </>
        )
        if (layout == EContactCardLayout.threeColsAndForm) {
          infoItems = (<>
            <div className="flex flex-col py-7">
              {mobilePhoneInfo}
              {emailInfo}
            </div>
            {showOffices && officeInfoPosition == EOfficeInfoPosition.card && offices.length > 0 &&
              <>
                <div className="h-px bg-divider w-full"></div>
                {officesInfo}
              </>
            }
          </>)
        }
        return <>
          {infoItems}
        </>;
      case ECardIconsLayout.columns:
        return (
          <div className={`flex flex-col gap-y-2.5 md:flex-row ${showOffices ? "lg:flex-col xl:flex-row xl:gap-x-7 my-4" : ""}`}>
            <div className="flex flex-col gap-y-2.5 md:pr-5">
              {layout == EContactCardLayout.twoColsAndForm && (
                <>
                  {mobilePhoneInfo}
                  {emailInfo}
                  {websiteInfo}                  
                  {socialInfo}
                </>
              )}
              {layout != EContactCardLayout.twoColsAndForm && (
                <>
                  {mobilePhoneInfo}
                  {officePhoneInfo}
                  {vCard}
                </>
              )}
            </div>
            <div className="hidden md:block md:border-l"></div>
            <div className={`flex flex-col gap-y-2.5 md:pl-5 ${showOffices ? "flex-grow lg:pl-0 xl:pl-5" : ""}`}>
              {officeInfoPosition == EOfficeInfoPosition.card && layout == EContactCardLayout.twoColsAndForm && (
                <>
                  {officesInfo}
                </>
              )}
              {layout != EContactCardLayout.twoColsAndForm && (
                <>
                  {emailInfo}
                  {addresInfo}
                </>
              )}
            </div>
          </div>
        )
      case ECardIconsLayout.columns1row:
        const rowItemClass = "lg:[&:first-child]:pl-0 lg:[&:first-child]:pr-4 lg:[&:last-child]:pl-4 lg:[&:last-child]:pr-0 px-4"
        return (
          <div className="flex flex-col items-center lg:justify-start lg:flex-row flex-wrap lg:divide-x gap-y-1">
            {officePhoneInfo && (<div className={rowItemClass}>{officePhoneInfo}</div>)}
            {mobilePhoneInfo && (<div className={rowItemClass}>{mobilePhoneInfo}</div>)}
            {addresInfo && (<div className={rowItemClass}>{addresInfo}</div>)}
            {vCard && (<div className={rowItemClass}>{vCard}</div>)}
            {websiteInfo && (<div className={rowItemClass}>{websiteInfo}</div>)}
            {socialInfo && (<div className={rowItemClass}>{socialInfo}</div>)}
          </div>
        )
    }
  })()

  const iconContextProviderProps: ProviderProps<IconContext> = {
    value: {
      size: "1.2em",
      className: `mr-2 inline-block text-right ${iconAccent
        ? `hover:text-body-text-inverted`
        : `text-body-text-inverted hover:text-accent`
        }`,
    }
  }

  contactUnderPhoto && (
    image = (<div className={`${layoutVariant.imageWrapperClasses} flex flex-col`}>
      {image}
      <IconContext.Provider
        {...iconContextProviderProps}
      >
        {contactContent}
      </IconContext.Provider>
    </div>)
  )

  return (
    <div
      className={`contact-card ${layoutVariant.containerClasses} ${className}`}
    >
      {image}
      <div
        className={`p-2 ${layoutVariant.infoClasses} ${layoutVariant.textClasses} ${agentInfoClassName}`}
      >
        {cardTitleContainer}
        {officeInfoPosition == EOfficeInfoPosition.bio && officesInfo}
        <IconContext.Provider
          {...iconContextProviderProps}
        >
          <>
            {!contactUnderPhoto && (contactContent)}
            {showBio && (layout === EContactCardLayout.pageSection || layout === EContactCardLayout.twoColsAndForm) && (
              <>
                <div className={`w-full whitespace-pre-line ${!showAgentBioReadMoreButton || showMoreBio ? '' : 'line-clamp-10'}`} dangerouslySetInnerHTML={{ __html: contact.bio ? contact.bio : "" }}></div>
                {showAgentBioReadMoreButton && (
                  <Button
                    line={true}
                    icon={false}
                    linkProps={{
                      className: `px-5 py-2 min-w-[140px] rounded-none border-0 capitalize font-normal italic my-2 inline-block`,
                      onClick: (evnt) => setShowMoreBio(!showMoreBio)
                    }}
                  >
                    {showMoreBio ? 'Show Less' : 'Show More'}
                  </Button>
                )}
              </>
            )}
            {buttonText && (
              <div className="whitespace-nowrap max-w-full">
                <Button
                  line={true}
                  icon={false}
                  linkProps={{
                    href: contact.path,
                    className: `px-5 py-2 min-w-[140px] rounded-none border-0 capitalize font-normal italic my-2 inline-block`,
                  }}
                  linkFunc={Linkf}
                >
                  {buttonText}
                </Button>
              </div>
            )}
          </>
        </IconContext.Provider>
      </div>
      {(layout == EContactCardLayout.threeColsAndForm || layout == EContactCardLayout.twoColsAndForm) &&
        <ContactForm
          title={formTitle}
          classNames={`strictTwoColumn ${layoutVariant.formClasses}`}
          inputTextAreaLabel={formTextAreaLabel}
          {...formProps}
          clickHandler={(showContactInfoOnFormSubmit || formProps?.clickHandler) ?
            async (formValue: any) => {
              if (showContactInfoOnFormSubmit) {
                setShowEmail(true)
                setShowMobilePhone(true)
                setShowOfficePhone(true)
              }
              formProps?.clickHandler && await formProps?.clickHandler(formValue)
            } : undefined}
        />
      }
    </div>
  );
};

export default ContactCard;
